import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { setLoginSuccessful } from "../store/authUser/authUserSlice";
import { session } from "../services/api";
import { setDataInLocalStorage } from "../utils/helpers";
import BaseLoader from "../components/ui/BaseLoader";

/**
 * Where the OAuth callback lands.
 *
 * Deliberately its own public route rather than "/": a redirect carries no response
 * body, so the tokens arrive in the query string and are not in localStorage yet.
 * Any guarded route would see an unauthenticated visitor and bounce them — and
 * RequireAuth bounces with a full page reload, which destroys the URL and the tokens
 * with it.
 */
const SocialCallbackPage = () => {
  const dispatch = useDispatch();

  // Read during the first render, before the effect below clears the URL, so a
  // re-render can never lose them.
  const tokensRef = useRef(null);
  if (tokensRef.current === null) {
    const params = new URLSearchParams(window.location.search);
    const accessToken = params.get("accessToken");
    const refreshToken = params.get("refreshToken");
    tokensRef.current = accessToken && refreshToken ? { accessToken, refreshToken } : false;
  }
  const tokens = tokensRef.current;

  const [stored, setStored] = useState(false);

  useEffect(() => {
    if (!tokens) return;

    // Must be in storage before session() goes out: axiosInstance reads the access
    // token from localStorage on every request.
    setDataInLocalStorage("accessToken", tokens.accessToken);
    setDataInLocalStorage("refreshToken", tokens.refreshToken);

    // Keep the tokens out of the history entry and out of anything that later reads
    // the address bar.
    window.history.replaceState({}, document.title, window.location.pathname);

    setStored(true);
  }, [tokens]);

  const { data: user, isError } = useQuery({
    queryKey: ["socialLoginUser"],
    queryFn: session,
    enabled: stored,
    retry: false,
  });

  useEffect(() => {
    if (!user || !tokens) return;

    // Same payload shape as password login, so storage and redirect-by-role behave
    // identically from here on.
    dispatch(setLoginSuccessful({ user, ...tokens }));
  }, [user, tokens, dispatch]);

  if (!tokens || isError) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <p className="text-dark text-base">We couldn’t finish signing you in.</p>
        <Link className="text-primary underline underline-offset-4 mt-4" to="/login">
          Back to login
        </Link>
      </div>
    );
  }

  // The same full-screen loader the rest of the app uses while a page resolves.
  return <BaseLoader isLoading={true} />;
};

export default SocialCallbackPage;
